export interface ActivatableSessions { activateMulticast(): void; }

export function makeSourceSwitchHandler(options: {
	multicastBridgeAvailable: boolean;
	sessionsReady: Promise<ActivatableSessions | null>;
	onMulticastConfirmed?(): void;
	onAbortTakeover?(): void;
	log?(message: string): void;
	warn?(message: string): void;
}): (source: string) => Promise<void> {
	const log = options.log ?? console.log;
	const warn = options.warn ?? console.warn;
	return async (source) => {
		if (source !== "multicast") return;
		if (!options.multicastBridgeAvailable) {
			warn("[MoqWatch] MediaRouter requested multicast without bridge; keeping MoQ active");
			return;
		}
		const sessions = await options.sessionsReady;
		if (!sessions) {
			warn("[MoqWatch] SessionManager unavailable; aborting multicast takeover, staying on MoQ");
			options.onAbortTakeover?.();
			return;
		}
		log("[MoqWatch] MediaRouter -> multicast; activating via SessionManager");
		sessions.activateMulticast();
		options.onMulticastConfirmed?.();
	};
}

export interface LossArcSession {
	/** TrackSession state. Optional so non-TrackSession hosts keep the generic API. */
	readonly state?: string;
	lossDetected(): void;
	lossRecovered(): void;
}
export interface FecStarvationSnapshot {
	sourceSymbols: number;
	repairSymbols: number;
	blocksComplete: number;
	blocksRecovered: number;
	mediaFrames?: number;
}
export interface StarvationDetectorLike { update(snapshot: FecStarvationSnapshot): void; reset(): void; }
export type StallDetectorLike = StarvationDetectorLike;

export function wireStallDetector(args: {
	session: LossArcSession | undefined;
	windowMs: number;
	makeDetector(config: { windowMs: number; onStarvation(): void; onRecovery(): void }): StallDetectorLike;
}): StallDetectorLike | null {
	if (!args.session || args.windowMs <= 0) return null;
	return args.makeDetector({
		windowMs: args.windowMs,
		onStarvation: () => args.session!.lossDetected(),
		onRecovery: () => args.session!.lossRecovered(),
	});
}

export async function wireStarvationDetector(args: {
	session: LossArcSession | undefined;
	trackName: string;
	fecInterleaveMs: number;
	fecK: number;
	deliveryWindowMs?: number;
	makeDetector(config: { windowMs: number; onStarvation(): void; onRecovery(): void }): StarvationDetectorLike;
	warn?(message: string): void;
}): Promise<StarvationDetectorLike | null> {
	if (!args.session || args.fecInterleaveMs <= 0 || args.fecK <= 0) return null;
	const windowMs = args.deliveryWindowMs ?? Math.ceil(args.fecInterleaveMs * 3);
	if (!Number.isFinite(windowMs) || windowMs <= 0) {
		args.warn?.(`Unable to create starvation detector for ${args.trackName}: invalid delivery window ${windowMs}`);
		return null;
	}
	const detector = wireStallDetector({
		session: args.session,
		windowMs,
		makeDetector: args.makeDetector,
	});
	if (!detector) args.warn?.(`Unable to create starvation detector for ${args.trackName}`);
	if (!detector || typeof args.session.state !== "string") return detector;

	// Stats flow while TrackSession is still `joining`. Feeding them directly can
	// age a starvation episode past its window before the first multicast RAP,
	// causing the first post-cutover sample to flip a healthy session to
	// `failing`. Start with a fresh baseline only once multicast/AMT is primary;
	// retain the detector through `failing` so it can observe recovery.
	let active = false;
	return {
		update(snapshot) {
			const state = args.session!.state;
			const shouldObserve = state === "multicast" || state === "amt" || state === "failing";
			if (!shouldObserve) {
				if (active) detector.reset();
				active = false;
				return;
			}
			if (!active) {
				detector.reset();
				active = true;
			}
			detector.update(snapshot);
		},
		reset() {
			active = false;
			detector.reset();
		},
	};
}
